// oxlint-disable max-lines

import type { ApiResponseTypes } from '@adverity/contracts'

import type { InsightsWorkspaceType, TargetBinding } from '../types/entities'

type Value = string | number | boolean | null

const toSearchParams = (
    object: undefined | Record<string, undefined | Value | Array<Value> | ReadonlyArray<Value>>,
) => {
    if (!object) return ''

    const searchParams = new URLSearchParams()

    for (const [key, value] of Object.entries(object)) {
        if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
                value.forEach((arrayValue) => {
                    if (arrayValue !== null) {
                        searchParams.append(key, String(arrayValue))
                    }
                })
            } else {
                searchParams.append(key, String(value))
            }
        }
    }

    const stringifiedParams = searchParams.toString()

    return stringifiedParams ? `?${stringifiedParams}` : ''
}

type BulkActionSearchParams =
    | {
          entity_ids: string
          exclude_entity_ids?: undefined
      }
    | {
          exclude_entity_ids: string
          entity_ids?: undefined
      }

type UnknownUrlParams = Record<string, string | Array<string>>

type DatastreamId = ApiResponseTypes['/api/datastreams/{datastream_id}/']['id']

/**
 * @deprecated
 * Please use contract based approach
 * If ninja api is not available, use
 * web-app/libs/contracts/legacy-contracts/paths/index.yaml
 * And declare them manually
 */
export const API_URLS = {
    authorization: {
        /**
         * @deprecated Use contract-based approach via @adverity/contracts:
         * apiOptions.query({ path: '/api/web-app/connection/{connectionId}/reauthenticate/' }, { params: { path: { connectionId } } })
         *
         * Note: This endpoint is kept for backward compatibility. New code should use the contract.
         */
        authorizationTypes: (connectionId: number) => `/api/web-app/connection/${connectionId}/reauthenticate/`,
        /**
         * @deprecated Use `apiOptions('/api/web-app/connection/{connectionId}/billing-objects/', 'get')` with the datatap-legacy-api contract instead.
         * This constant will be removed in a future version.
         * See the datatap-legacy-api.spec.json contract for the full endpoint definition.
         */
        billingObjects: (connectionId: number, searchParams?: { add_extra_info: 1 }) =>
            `/api/web-app/connection/${connectionId}/billing-objects/${toSearchParams(searchParams)}`,
        /**
         * @deprecated Use contract-based approach via @adverity/contracts:
         * apiOptions.query({ path: '/api/web-app/type-in-billing-objects/' }, { params: { query: { auth_id } } })
         *
         * Note: This endpoint is kept for backward compatibility. New code should use the contract.
         */
        typeInBillingObjects: (searchParams: { auth_id: string }) =>
            `/api/web-app/type-in-billing-objects/${toSearchParams(searchParams)}`,
        /**
         * @deprecated Use contract-based approach via @adverity/contracts:
         * apiOptions.mutation({ path: '/api/web-app/type-in-billing-objects/validate/' }, { params: { query: { auth_id } }, body: { value } })
         *
         * Note: This endpoint is kept for backward compatibility. New code should use the contract.
         */
        typeInBillingObjectsValidate: (searchParams: { auth_id: string }) =>
            `/api/web-app/type-in-billing-objects/validate/${toSearchParams(searchParams)}`,
        connectionTypes: '/api/available-connection-types/',
        /**
         * @deprecated Use contract-based approach via @adverity/contracts:
         * apiOptions.query({ path: '/api/connection/invite/' })
         *
         * Note: This endpoint is kept for backward compatibility. New code should use the contract.
         */
        createAuthorization: '/api/connection/invite/',
        /**
         * @deprecated Use contract-based approach via @adverity/contracts:
         * apiOptions.query({ path: '/api/web-app/connection/{connectionId}/reauthenticate/email-form/' }, { params: { path: { connectionId } } })
         *
         * Note: This endpoint is kept for backward compatibility. New code should use the contract.
         */
        emailFormData: (connectionId: number) => `/api/web-app/connection/${connectionId}/reauthenticate/email-form/`,
        exportAsCSV: (authorizationId: string) => `/api/web-app/authorization/${authorizationId}/export-as-csv/`,
        fetchHistory: (authorizationId: string) => `/api/web-app/revisions/core/authorization/${authorizationId}/`,
        /**
         * @deprecated Use contract-based approach via @adverity/contracts:
         * apiOptions.query({ path: '/api/update-metadata-jobs/' }, { params: { query: { auth_id, ordering: '-job_start' } } })
         *
         * For mocking in tests:
         * - Storybook/Ladle: mswHttp.get('/api/update-metadata-jobs/', handler)
         *
         * Note: This endpoint is kept for backward compatibility. New code should use the contract.
         */
        jobMetadata: (searchParams: { auth_id: string; ordering: '-job_start' }) =>
            `/api/update-metadata-jobs/${toSearchParams(searchParams)}`,
    },
    /**
     * @deprecated Use contract-based approach via @adverity/contracts:
     * apiOptions.query({ path: '/api/ads/' }, { params: { query: { check: '' } } })
     *
     * Note: This endpoint is kept for backward compatibility. New code should use the contract.
     */
    adblock: (searchParams: { check: '' }) => `/api/ads/${toSearchParams(searchParams)}`,
    /**
     * @deprecated Use contract-based approach via @adverity/contracts:
     * cyIntercept('/api/web-app/upgrade-plan-contact/', 'post', 200, {})
     *
     * Note: This endpoint is kept for backward compatibility. New code should use the contract.
     */
    upgradePlanContact: '/api/web-app/upgrade-plan-contact/',
    /**
     * @deprecated Use contract-based approach via @adverity/contracts:
     * apiOptions.query({ path: '/api/web-app/dataschema/' })
     *
     * For mocking in tests:
     * - Cypress: cyIntercept('/api/web-app/dataschema/', 'get', 200, mockData)
     * - Storybook/Ladle: mswHttp.get('/api/web-app/dataschema/', handler)
     *
     * Note: This endpoint is kept for backward compatibility. New code should use the contract.
     */
    dataSchema: '/api/web-app/dataschema/',
    bindings: {
        index: '/api/web-app/bindings/',
        details: (id: TargetBinding['id']) => `/api/web-app/bindings/${id}/`,
    },
    columnMapping: '/api/web-app/columns/',
    connectorEngine: {
        /**
         * @deprecated Use contract-based approach via @adverity/contracts:
         * apiOptions.query({ path: '/api/connector-engine/config/{connectorSlug}/' }, { params: { path: { connectorSlug } } })
         *
         * Response type: string (YAML configuration)
         */
        config: (connectorSlug: string) => `/api/connector-engine/config/${connectorSlug}/`,
        /**
         * @deprecated Use contract-based approach via @adverity/contracts:
         * apiOptions.query({ path: '/api/connector-engine/connectors/' })
         *
         * Response type: Array<ConnectorEngineConnector>
         */
        connectors: '/api/connector-engine/connectors/',
        /**
         * @deprecated Use contract-based approach via @adverity/contracts:
         * apiOptions.query({ path: '/api/connector-engine/jsonschema/' })
         *
         * Response type: JsonSchema (JSON Schema document for connector engine YAML configurations)
         */
        jsonSchema: '/api/connector-engine/jsonschema/',
        /**
         * @deprecated Use contract-based approach via @adverity/contracts:
         * apiOptions.query({ path: '/api/connector-engine/reference/' })
         *
         * Response type: string (Markdown-formatted reference documentation)
         */
        jsonSchemaReference: '/api/connector-engine/reference/',
        /**
         * @deprecated Use contract-based approach via @adverity/contracts:
         * apiOptions.query({ path: '/api/connector-engine/workflow/{connectorSlug}/{workflow}/' })
         *
         * Response type: ConnectorEngineWorkflowResponse (Workflow visualization embed URL)
         */
        workflow: (connectorSlug: string, workflow: string) =>
            `/api/connector-engine/workflow/${connectorSlug}/${workflow}/`,
        /**
         * @deprecated Use contract-based approach via @adverity/contracts:
         * apiOptions.query({ path: '/api/connector-engine/workflow-context/{connectorSlug}/' })
         *
         * Response type: ApiResponseTypes['/api/connector-engine/workflow-context/{connectorSlug}/']
         */
        workflowContext: (connectorSlug: string) => `/api/connector-engine/workflow-context/${connectorSlug}/`,
    },
    /**
     * @deprecated Use contract-based approach via @adverity/contracts:
     * apiOptions.query({ path: '/api/web-app/datasources/' })
     *
     * Note: This endpoint is kept for backward compatibility. New code should use the contract.
     */
    datasources: (searchParams: { stack_id: number } | { page_size: number }) =>
        `/api/web-app/datasources/${toSearchParams(searchParams)}`,
    datastream: (searchParams?: { name?: string; stack_id?: number; creator?: string; page_size?: number }) =>
        `/api/datastreams/${toSearchParams(searchParams)}`,
    datastreamDetail: (datastreamId: DatastreamId) => `/api/datastreams/${datastreamId}/`,
    datastreamDetailStats: (datastreamId: DatastreamId) => `/api/datastreams/${datastreamId}/stats/`,
    datastreamDetailDefaultMapping: (datastreamId: DatastreamId) => `/api/datastreams/${datastreamId}/default-mapping/`,
    datastreamDetailPassiveDestinations: (datastreamId: DatastreamId) =>
        `/api/datastreams/${datastreamId}/passive-destinations/`,
    datastreamDetailSchedules: (datastreamId: DatastreamId) => `/api/datastreams/${datastreamId}/schedules/`,
    datastreams: {
        /**
         * @deprecated Use contract-based approach via @adverity/contracts:
         * apiOptions.query({ path: '/api/web-app/datastreams/{datastream_id}/admin-actions/' }, { params: { path: { datastream_id } } })
         *
         * Note: This endpoint is kept for backward compatibility. New code should use the contract.
         */
        adminActions: (datastreamId: DatastreamId) => `/api/web-app/datastreams/${datastreamId}/admin-actions/`,
        confirmDelete: '/api/web-app/datastreams/confirm-delete/',
        /**
         * @deprecated Use contract-based approach via @adverity/contracts:
         * ApiPostResponseTypes['/api/web-app/datastreams/{datastream_id}/cancel_running_or_scheduled_jobs/']
         *
         * Note: This endpoint is kept for backward compatibility. New code should use the contract.
         */
        datastreamCancelRunningOrScheduledJobs: (datastreamId: DatastreamId) =>
            `/api/web-app/datastreams/${datastreamId}/cancel_running_or_scheduled_jobs/`,
        /**
         * @deprecated Use contract-based approach via @adverity/contracts:
         * apiOptions.query({ path: '/api/web-app/datastreams/{datastream_id}/clone/' }, { params: { path: { datastream_id } } })
         *
         * Note: This endpoint is kept for backward compatibility. New code should use the contract.
         */
        datastreamClone: (datastreamId: DatastreamId) => `/api/web-app/datastreams/${datastreamId}/clone/`,
        datastreamSettingsForm: (datastreamId: DatastreamId, isRetentionSettings: boolean) =>
            // actually two paths:
            // - /api/web-app/datastream_settings/{datastream_id}/
            // - /api/web-app/datastream_settings/{datastream_id}/retention/
            `/api/web-app/datastream_settings/${datastreamId}/${isRetentionSettings ? 'retention/' : ''}`,
        datastreamSettingsWarehouse: (datastreamId: DatastreamId) =>
            `/api/web-app/datastream_settings/${datastreamId}/has_any_warehouse_imports/`,
        deleteDatastream: '/api/web-app/datastreams/actions/delete/',
        /**
         * @deprecated Use contract-based approach via @adverity/contracts:
         * apiOptions.query({ path: '/api/web-app/datastreams/{datastream_id}/' }, { params: { path: { datastream_id } } })
         *
         * Note: This endpoint is kept for backward compatibility. New code should use the contract.
         */
        details: (datastreamId: DatastreamId) => `/api/web-app/datastreams/${datastreamId}/`,
        extractionLogs: {
            /**
             * @deprecated Use contract-based approach via @adverity/contracts:
             * apiOptions.mutation({ path: '/api/web-app/datastreams/{datastream_id}/extraction-logs/actions/acknowledge/', method: 'put' })
             *
             * Note: This endpoint is kept for backward compatibility. New code should use the contract.
             */
            acknowledge: (
                datastreamId: DatastreamId,
                searchParams: BulkActionSearchParams,
                urlParams: UnknownUrlParams,
            ) =>
                `/api/web-app/datastreams/${datastreamId}/extraction-logs/actions/acknowledge/${toSearchParams({ ...urlParams, ...searchParams })}`,
            /**
             * @deprecated Use contract-based approach via @adverity/contracts:
             * apiOptions.mutation({ path: '/api/web-app/datastreams/{datastream_id}/extraction-logs/actions/retry/', method: 'put' })
             *
             * Note: This endpoint is kept for backward compatibility. New code should use the contract.
             */
            retry: (datastreamId: DatastreamId, searchParams: BulkActionSearchParams, urlParams: UnknownUrlParams) =>
                `/api/web-app/datastreams/${datastreamId}/extraction-logs/actions/retry/${toSearchParams({ ...urlParams, ...searchParams })}`,
            /**
             * @deprecated Use contract-based approach via @adverity/contracts:
             * apiOptions.mutation({ path: '/api/web-app/datastreams/{datastream_id}/extraction-logs/actions/unacknowledge/', method: 'put' })
             *
             * Note: This endpoint is kept for backward compatibility. New code should use the contract.
             */
            unacknowledge: (
                datastreamId: DatastreamId,
                searchParams: BulkActionSearchParams,
                urlParams: UnknownUrlParams,
            ) =>
                `/api/web-app/datastreams/${datastreamId}/extraction-logs/actions/unacknowledge/${toSearchParams({ ...urlParams, ...searchParams })}`,
        },
        extracts: {
            list: (datastreamId: DatastreamId) => `/api/web-app/datastreams/${datastreamId}/datastream-extracts/`,
            /**
             * @deprecated Use contract-based approach via @adverity/contracts:
             * cyIntercept('/api/web-app/datastreams/{datastream_id}/datastream-extracts/actions/schedule-delete/', 'put', 200, { success: true }, { pathParams: { datastream_id }, queryParams: { entity_ids: '...' } })
             *
             * Note: This endpoint is kept for backward compatibility. New code should use the contract.
             */
            scheduleDelete: (
                datastreamId: DatastreamId,
                searchParams: BulkActionSearchParams,
                urlParams: UnknownUrlParams,
            ) =>
                `/api/web-app/datastreams/${datastreamId}/datastream-extracts/actions/schedule-delete/${toSearchParams({ ...urlParams, ...searchParams })}`,
            /**
             * @deprecated Use contract-based approach via @adverity/contracts:
             * cyIntercept('/api/web-app/datastreams/{datastream_id}/datastream-extracts/actions/undelete/', 'put', 200, { success: true }, { pathParams: { datastream_id }, queryParams: { entity_ids: '...' } })
             *
             * Note: This endpoint is kept for backward compatibility. New code should use the contract.
             */
            undelete: (datastreamId: DatastreamId, searchParams: BulkActionSearchParams, urlParams: UnknownUrlParams) =>
                `/api/web-app/datastreams/${datastreamId}/datastream-extracts/actions/undelete/${toSearchParams({ ...urlParams, ...searchParams })}`,
            /**
             * @deprecated Use contract-based approach via @adverity/contracts:
             * cyIntercept('/api/web-app/datastreams/{datastream_id}/datastream-extracts/actions/requeue/', 'put', 200, { success: true }, { pathParams: { datastream_id }, queryParams: { entity_ids: '...' } })
             *
             * Note: This endpoint is kept for backward compatibility. New code should use the contract.
             */
            requeue: (
                datastreamId: DatastreamId,
                searchParams: BulkActionSearchParams & { binding_id?: number },
                urlParams: UnknownUrlParams,
            ) =>
                `/api/web-app/datastreams/${datastreamId}/datastream-extracts/actions/requeue/${toSearchParams({ ...urlParams, ...searchParams })}`,
            /**
             * @deprecated Use contract-based approach via @adverity/contracts:
             * cyIntercept('/api/web-app/datastreams/{datastream_id}/datastream-extracts/actions/requeue-sequential/', 'put', 200, { success: true }, { pathParams: { datastream_id }, queryParams: { entity_ids: '...' } })
             *
             * Note: This endpoint is kept for backward compatibility. New code should use the contract.
             */
            requeue_sequential: (
                datastreamId: DatastreamId,
                searchParams: BulkActionSearchParams,
                urlParams: UnknownUrlParams,
            ) =>
                `/api/web-app/datastreams/${datastreamId}/datastream-extracts/actions/requeue-sequential/${toSearchParams({ ...urlParams, ...searchParams })}`,
            /**
             * @deprecated Use contract-based approach via @adverity/contracts:
             * cyIntercept('/api/web-app/datastreams/{datastream_id}/datastream-extracts/actions/update-metadata/', 'put', 200, { success: true }, { pathParams: { datastream_id }, queryParams: { entity_ids: '...' } })
             *
             * Note: This endpoint is kept for backward compatibility. New code should use the contract.
             */
            update_metadata: (
                datastreamId: DatastreamId,
                searchParams: BulkActionSearchParams,
                urlParams: UnknownUrlParams,
            ) =>
                `/api/web-app/datastreams/${datastreamId}/datastream-extracts/actions/update-metadata/${toSearchParams({ ...urlParams, ...searchParams })}`,
            /**
             * @deprecated Use contract-based approach via @adverity/contracts:
             * cyIntercept('/api/web-app/datastreams/{datastream_id}/datastream-extracts/actions/apply-transformations/', 'put', 200, { success: true }, { pathParams: { datastream_id }, queryParams: { entity_ids: '...' } })
             *
             * Note: This endpoint is kept for backward compatibility. New code should use the contract.
             */
            apply_transformations: (
                datastreamId: DatastreamId,
                searchParams: BulkActionSearchParams,
                urlParams: UnknownUrlParams,
            ) =>
                `/api/web-app/datastreams/${datastreamId}/datastream-extracts/actions/apply-transformations/${toSearchParams({ ...urlParams, ...searchParams })}`,
        },
    },
    enrichments: {
        createEnrichmentPreviews: '/api/web-app/enrichment-previews/',
        datastreamEnrichments: (datastreamId: DatastreamId) =>
            `/api/web-app/enrichment/datastreams/${datastreamId}/enrichments/`,
        getEnrichmentPreviews: (id: number) => `/api/web-app/enrichment-previews/${id}/`,
        getEnrichmentPreviewsData: (id: number, searchParams: { page_size: number }, urlParams: UnknownUrlParams) =>
            `/api/web-app/enrichment-previews/${id}/data/${toSearchParams({ ...urlParams, ...searchParams })}`,
        getEnrichmentPreviewsExtract: (id: number) => `/api/web-app/enrichment-previews/${id}/extract/`,
        list: (searchParams: { for_datastream: number }) => `/api/web-app/enrichments/${toSearchParams(searchParams)}`,
    },
    extractPreview: {
        /**
         * @deprecated Use contract-based approach via @adverity/contracts:
         * apiOptions.mutation({ path: '/api/web-app/datastream-extracts/actions/apply-transformations/', method: 'put' })
         *
         * Note: This endpoint is kept for backward compatibility. New code should use the contract.
         */
        applyTransformations: (searchParams: { entity_ids: number }) =>
            `/api/web-app/datastream-extracts/actions/apply-transformations/${toSearchParams(searchParams)}`,
        /**
         * @deprecated Use contract-based approach via @adverity/contracts:
         * cyIntercept('/api/web-app/datastream-extracts/confirm-delete/', 'get', 200, confirmDeleteMock, { queryParams: { entity_ids: '...' } })
         *
         * Note: This endpoint is kept for backward compatibility. New code should use the contract.
         */
        confirmDelete: '/api/web-app/datastream-extracts/confirm-delete/',
        /**
         * @deprecated Use contract-based approach via @adverity/contracts:
         * cyIntercept('/api/web-app/datastream-extracts/actions/delete/', 'put', 200, { success: true }, { queryParams: { entity_ids: '...', confirmed: 'true' } })
         *
         * Note: This endpoint is kept for backward compatibility. New code should use the contract.
         */
        deleteAction: '/api/web-app/datastream-extracts/actions/delete/',
        download: (id: number, searchParams?: { fileformat: 'csv' | 'json' | 'xlsx' }) =>
            `/api/extracts/${id}/download/${toSearchParams(searchParams)}`,
        /**
         * @deprecated Use contract-based approach via @adverity/contracts:
         * apiOptions.query({ path: '/api/web-app/datastream-extracts/{extracts_id}/preview/' })
         *
         * Note: This endpoint is kept for backward compatibility. New code should use the contract.
         */
        extractPreview: (id: number, searchParams: { page_size: number }, urlParams: UnknownUrlParams) =>
            `/api/web-app/datastream-extracts/${id}/preview/${toSearchParams({ ...urlParams, ...searchParams })}`,
        /**
         * @deprecated Use contract-based approach via @adverity/contracts:
         * cyIntercept('/api/web-app/datastream-extracts/actions/requeue/', 'put', 200, { success: true }, { queryParams: { entity_ids: '...' } })
         *
         * Note: This endpoint is kept for backward compatibility. New code should use the contract.
         */
        requeue: (searchParams: { entity_ids: number; binding_id?: number }) =>
            `/api/web-app/datastream-extracts/actions/requeue/${toSearchParams(searchParams)}`,
    },
    extractionLogs: {
        /**
         * @deprecated Use contract-based approach via @adverity/contracts:
         * apiOptions.mutation({ path: '/api/web-app/extraction-logs/actions/acknowledge/', method: 'put' })
         *
         * Note: This endpoint is kept for backward compatibility. New code should use the contract.
         */
        acknowledge: (searchParams: BulkActionSearchParams, urlParams: UnknownUrlParams) =>
            `/api/web-app/extraction-logs/actions/acknowledge/${toSearchParams({ ...urlParams, ...searchParams })}`,
        /**
         * @deprecated Use contract-based approach via @adverity/contracts:
         * apiOptions.mutation({ path: '/api/web-app/extraction-logs/actions/retry/', method: 'put' })
         *
         * Note: This endpoint is kept for backward compatibility. New code should use the contract.
         */
        retry: (searchParams: BulkActionSearchParams, urlParams: UnknownUrlParams) =>
            `/api/web-app/extraction-logs/actions/retry/${toSearchParams({ ...urlParams, ...searchParams })}`,
        /**
         * @deprecated Use contract-based approach via @adverity/contracts:
         * apiOptions.mutation({ path: '/api/web-app/extraction-logs/actions/unacknowledge/', method: 'put' })
         *
         * Note: This endpoint is kept for backward compatibility. New code should use the contract.
         */
        unacknowledge: (searchParams: BulkActionSearchParams, urlParams: UnknownUrlParams) =>
            `/api/web-app/extraction-logs/actions/unacknowledge/${toSearchParams({ ...urlParams, ...searchParams })}`,
    },
    extracts: {
        actions: {
            /**
             * @deprecated Use contract-based approach via @adverity/contracts:
             * cyIntercept('/api/web-app/datastream-extracts/actions/apply-transformations/', 'put', 200, { success: true }, { queryParams: { entity_ids: '...' } })
             *
             * Note: This endpoint is kept for backward compatibility. New code should use the contract.
             */
            apply_transformations: (searchParams: BulkActionSearchParams, urlParams: UnknownUrlParams) =>
                `/api/web-app/datastream-extracts/actions/apply-transformations/${toSearchParams({ ...urlParams, ...searchParams })}`,
            /**
             * @deprecated Use contract-based approach via @adverity/contracts:
             * cyIntercept('/api/web-app/datastream-extracts/actions/requeue/', 'put', 200, { success: true }, { queryParams: { entity_ids: '...' } })
             *
             * Note: This endpoint is kept for backward compatibility. New code should use the contract.
             */
            requeue: (searchParams: BulkActionSearchParams, urlParams: UnknownUrlParams) =>
                `/api/web-app/datastream-extracts/actions/requeue/${toSearchParams({ ...urlParams, ...searchParams })}`,
            /**
             * @deprecated Use contract-based approach via @adverity/contracts:
             * cyIntercept('/api/web-app/datastream-extracts/actions/requeue-sequential/', 'put', 200, { success: true }, { queryParams: { entity_ids: '...' } })
             *
             * Note: This endpoint is kept for backward compatibility. New code should use the contract.
             */
            requeue_sequential: (searchParams: BulkActionSearchParams, urlParams: UnknownUrlParams) =>
                `/api/web-app/datastream-extracts/actions/requeue-sequential/${toSearchParams({ ...urlParams, ...searchParams })}`,
            /**
             * @deprecated Use contract-based approach via @adverity/contracts:
             * cyIntercept('/api/web-app/datastream-extracts/actions/schedule-delete/', 'put', 200, { success: true }, { queryParams: { entity_ids: '...' } })
             *
             * Note: This endpoint is kept for backward compatibility. New code should use the contract.
             */
            scheduleDelete: (searchParams: BulkActionSearchParams, urlParams: UnknownUrlParams) =>
                `/api/web-app/datastream-extracts/actions/schedule-delete/${toSearchParams({ ...urlParams, ...searchParams })}`,
            /**
             * @deprecated Use contract-based approach via @adverity/contracts:
             * cyIntercept('/api/web-app/datastream-extracts/actions/undelete/', 'put', 200, { success: true }, { queryParams: { entity_ids: '...' } })
             *
             * Note: This endpoint is kept for backward compatibility. New code should use the contract.
             */
            undelete: (searchParams: BulkActionSearchParams, urlParams: UnknownUrlParams) =>
                `/api/web-app/datastream-extracts/actions/undelete/${toSearchParams({ ...urlParams, ...searchParams })}`,
            /**
             * @deprecated Use contract-based approach via @adverity/contracts:
             * cyIntercept('/api/web-app/datastream-extracts/actions/update-metadata/', 'put', 200, { success: true }, { queryParams: { entity_ids: '...' } })
             *
             * Note: This endpoint is kept for backward compatibility. New code should use the contract.
             */
            update_metadata: (searchParams: BulkActionSearchParams, urlParams: UnknownUrlParams) =>
                `/api/web-app/datastream-extracts/actions/update-metadata/${toSearchParams({ ...urlParams, ...searchParams })}`,
        },
    },
    /**
     * @deprecated Use contract-based approach via @adverity/contracts:
     * ```typescript
     * import type { ApiPostResponseTypes } from '@adverity/contracts'
     * type FormConfigResponse = ApiPostResponseTypes['/api/web-app/forms/{name}/']
     * type FieldConfig = ApiPostResponseTypes['/api/web-app/forms/{name}/field/']
     * ```
     *
     * Or use the direct contract type exports:
     * ```typescript
     * import type { FormConfigResponse, FieldConfig } from '@adverity/contracts/contracts/legacy'
     * ```
     *
     * For Cypress tests: `cyIntercept('/api/web-app/forms/{name}/', 'post', 200, mockData, { pathParams: { name: 'warehousebinding' } })`
     * For MSW/Storybook: `mswHttp.post('/api/web-app/forms/{name}/', ({ response }) => response(200).json(mockData))`
     */
    forms: {
        config: (name: string) => `/api/web-app/forms/${name}/`,
        field: (name: string) => `/api/web-app/forms/${name}/field/`,
    },
    /**
     * @deprecated Use contract-based approach via @adverity/contracts:
     * ```typescript
     * import type { ApiResponseTypes } from '@adverity/contracts'
     * type InsightsWorkspacesResponse = ApiResponseTypes['/api/web-app/insights/']
     * ```
     *
     * For Cypress tests: `cyIntercept('/api/web-app/insights/', 'get', 200, mockData)`
     * For MSW/Storybook: `mswHttp.get('/api/web-app/insights/', ({ response }) => response(200).json(mockData))`
     */
    insightsWorkspaces: '/api/web-app/insights/',
    /**
     * @deprecated Use contract-based approach via @adverity/contracts.
     * ```typescript
     * import type { ApiResponseTypes } from '@adverity/contracts'
     * type InsightsGlobalWorkspacesResponse = ApiResponseTypes['/api/web-app/insights/global/workspaces/']
     * ```
     *
     * For Cypress tests: `cyIntercept('/api/web-app/insights/global/workspaces/', 'get', 200, mockData)`
     * For MSW/Storybook: `mswHttp.get('/api/web-app/insights/global/workspaces/', ({ response }) => response(200).json(mockData))`
     */
    insightsWorkspacesGlobal: '/api/web-app/insights/global/workspaces/',
    /**
     * @deprecated Use contract-based approach via @adverity/contracts:
     * ```typescript
     * import type { ApiResponseTypes } from '@adverity/contracts'
     * type InsightsTargetWorkspacesResponse = ApiResponseTypes['/api/web-app/insights/{targetId}/workspaces/']
     * ```
     *
     * For Cypress tests: `cyIntercept('/api/web-app/insights/{targetId}/workspaces/', 'get', 200, mockData, { pathParams: { targetId: 123 } })`
     * For MSW/Storybook: `mswHttp.get('/api/web-app/insights/{targetId}/workspaces/', ({ response }) => response(200).json(mockData))`
     */
    insightsWorkspacesDetail: (targetId: number) => `/api/web-app/insights/${targetId}/workspaces/`,
    jobActions: {
        /**
         * @deprecated Use contract-based approach via @adverity/contracts.
         * For Cypress tests: `cyIntercept('/api/web-app/jobs/{jobId}/delete_extracts/', 'post', 200, { status: 'ok', message: '' }, { pathParams: { jobId: 123 } })`
         *
         * See datatap-legacy-api.spec.json for the full endpoint definition (JobActionResponse schema).
         */
        deleteExtracts: (jobId: number, searchParams?: { extracts__id: number }) =>
            `/api/web-app/jobs/${jobId}/delete_extracts/${toSearchParams(searchParams)}`,
        /**
         * @deprecated Use contract-based approach via @adverity/contracts.
         * For Cypress tests: `cyIntercept('/api/web-app/jobs/{jobId}/enrich_then_import_extracts/', 'post', 200, { status: 'ok', message: '' }, { pathParams: { jobId: 123 } })`
         *
         * See datatap-legacy-api.spec.json for the full endpoint definition (JobActionResponse schema).
         */
        enrichThenImportExtracts: (jobId: number, searchParams?: { sequentially: true } | { extracts__id: number }) =>
            `/api/web-app/jobs/${jobId}/enrich_then_import_extracts/${toSearchParams(searchParams)}`,
        /**
         * @deprecated Use contract-based approach via @adverity/contracts.
         * For Cypress tests: `cyIntercept('/api/web-app/jobs/{jobId}/update_extracts_metadata/', 'post', 200, { status: 'ok', message: '' }, { pathParams: { jobId: 123 } })`
         *
         * See datatap-legacy-api.spec.json for the full endpoint definition (JobActionResponse schema).
         */
        updateExtractsMetadata: (jobId: number, searchParams?: { extracts__id: number }) =>
            `/api/web-app/jobs/${jobId}/update_extracts_metadata/${toSearchParams(searchParams)}`,
    },
    /**
     * @deprecated Use contract-based approach via @adverity/contracts.
     * For Cypress tests: `cyIntercept('/api/web-app/job-issues/', 'get', 200, { results: [] }, { queryParams: {...} })`
     *
     * See datatap-legacy-api.spec.json for the full endpoint definition (JobIssuesResponse schema).
     */
    jobIssues: (searchParams: {
        job__job_group_id: number
        stage: 'enrich' | 'fetch' | 'transfer'
        type: string
        ordering: string
        error_cause?: number | null
    }) => {
        // follow up:  https://adverity.atlassian.net/browse/PET-1689
        const { error_cause, ...rest } = searchParams
        const params = typeof error_cause === 'number' ? { ...rest, error_cause } : rest

        return `/api/web-app/job-issues/${toSearchParams(params)}`
    },
    /**
     * @deprecated Use contract-based approach via @adverity/contracts.
     * For Cypress tests: `cyIntercept('/api/web-app/jobs/', 'get', 200, mockData, { queryParams: '*' })`
     * For Storybook/MSW: `mswHttp.get('/api/web-app/jobs/', ({ response }) => response(200).json(mockData))`
     *
     * See datatap-legacy-api.spec.json for the full endpoint definition (JobsResponse schema).
     */
    jobs: (
        searchParams: {
            state?: 'failed' | 'scheduled' | 'running' | 'success' | ''
            ack?: 'false'
            has_warnings?: 'true'
        } & (
            | {
                  datastream_id?: number
              }
            | {
                  stack_id?: number
              }
        ),
    ) => `/api/web-app/jobs/${toSearchParams(searchParams)}`,
    /** @deprecated Use contract path '/api/web-app/jobs/{id}/' with cyIntercept/mswHttp instead */
    jobsDetail: (id: number) => `/api/web-app/jobs/${id}/`,
    /** @deprecated Use contract path '/api/web-app/jobs/{id}/extracts_details/' with cyIntercept/mswHttp instead */
    jobsDetailExtractDetails: (id: number) => `/api/web-app/jobs/${id}/extracts_details/`,
    /**
     * @deprecated Use contract-based approach via @adverity/contracts:
     * Import type: `import type { ApiResponseTypes } from '@adverity/contracts'`
     * Response type: `ApiResponseTypes['/api/web-app/jobs/daily-stats/']`
     * For mocking: Use contract path '/api/web-app/jobs/daily-stats/' with cyIntercept/mswHttp
     */
    jobs_daily_stats: (searchParams: { stack_id: number }) =>
        `/api/web-app/jobs/daily-stats/${toSearchParams(searchParams)}`,
    /** @deprecated Use contract path '/logout/' with cyIntercept/mswHttp instead */
    logout: '/logout/',
    marketplace: {
        context: '/api/web-app/marketplace/',
    },
    performance: {
        /**
         * @deprecated Use contract-based approach via @adverity/contracts:
         * - Types: import type { Outlier, OutlierType } from '~/features/performance-manager/types'
         * - Mock data: use cyIntercept/mswHttp with '/api/web-app/performance/outliers/'
         */
        outliers: (searchParams: { stack_id: number }) =>
            `/api/web-app/performance/outliers/${toSearchParams(searchParams)}`,
        /**
         * @deprecated Use contract-based approach via @adverity/contracts:
         * - Types: import type { Recommendation, RecommendationsResponse } from '~/features/performance-manager/types'
         * - Mock data: use mswHttp with '/api/web-app/performance/recommendations/'
         *
         * Note: This endpoint is kept for backward compatibility. New code should use the contract.
         */
        recommendations: '/api/web-app/performance/recommendations/',
        /**
         * @deprecated Use contract-based approach via @adverity/contracts:
         * - Delete endpoint: '/api/web-app/performance/recommendations/{id}/'
         *
         * Note: This endpoint is kept for backward compatibility. New code should use the contract.
         */
        recommendationsDetail: (id: number) => `/api/web-app/performance/recommendations/${id}`,
        /**
         * @deprecated Use contract-based approach via @adverity/contracts:
         * apiOptions.query({ path: '/api/web-app/performance/task-durations/' }, { params: { query: { stack_id, scheduled_time, ... } } })
         *
         * Note: This endpoint is kept for backward compatibility. New code should use the contract.
         */
        taskDurations: (searchParams: {
            task_duration_time__lte?: string
            task_duration_time__gte?: string
            scheduled_time?: string
            stack_id?: number
            datasource_id?: number
        }) => `/api/web-app/performance/task-durations/${toSearchParams(searchParams)}`,
    },
    resetPassword: ({ uidb64, token }: { uidb64: string; token: string }) =>
        `/api/web-app/accounts/reset/${uidb64}/${token}/`,
    /**
     * @deprecated Use contract-based approach via @adverity/contracts:
     * apiOptions.query({ path: '/api/web-app/revision-changes/{revision_id}/' })
     *
     * Note: This endpoint is kept for backward compatibility. New code should use the contract.
     */
    revisionChange: (id: number) => `/api/web-app/revision-changes/${id}/`,
    revisions: (app: string, model: string, id: number) => `/api/web-app/revisions/${app}/${model}/${id}/`,
    schemaColumnsMasks: {
        perBinding: (bindingId: number) => `/api/internal/ui/v1/bindings/${bindingId}/schema_column_masks/`,
        perSchemaColumnInfo: (schemaColumnInfoId: number) =>
            `/api/internal/ui/v1/columns/${schemaColumnInfoId}/overwrites/`,
    },
    scheduling: {
        'smart-schedules': {
            /**
             * @deprecated Use contract-based approach via @adverity/contracts:
             * apiOptions.query({ path: '/api/web-app/smart-schedules/available-time-ranges/' })
             *
             * Query params: { smart_schedule_id?: number; datastream_id?: number }
             */
            availableTimeRanges: (searchParams: { smart_schedule_id?: number; datastream_id?: number }) =>
                `/api/web-app/smart-schedules/available-time-ranges/${toSearchParams(searchParams)}`,
            /**
             * @deprecated Use contract-based approach via @adverity/contracts:
             * apiOptions.query({ path: '/api/web-app/smart-schedules/' })
             *
             * Query params: { datastream_id?: number }
             */
            list: (searchParams?: { datastream_id: number }) =>
                `/api/web-app/smart-schedules/${toSearchParams(searchParams)}`,
            /**
             * @deprecated Use contract-based approach via @adverity/contracts:
             * api.GET('/api/web-app/smart-schedules/{smart_schedule_id}/')
             * api.PATCH('/api/web-app/smart-schedules/{smart_schedule_id}/')
             * api.DELETE('/api/web-app/smart-schedules/{smart_schedule_id}/')
             */
            item: (id: number) => `/api/web-app/smart-schedules/${id}/`,
            /**
             * @deprecated Use contract-based approach via @adverity/contracts:
             * apiOptions.query({ path: '/api/web-app/smart-schedules/status/' })
             *
             * Query params form a discriminated union:
             * - { smart_schedule_id: number } for existing schedules
             * - { datastream_id, time_range_preset, offset_days, fixed_start?, fixed_end? } for new schedule preview
             */
            status: (
                searchParams:
                    | {
                          smart_schedule_id: number
                      }
                    | {
                          datastream_id: number
                          time_range_preset: number
                          fixed_start?: string
                          fixed_end?: string
                          offset_days: number
                      },
            ) => `/api/web-app/smart-schedules/status/${toSearchParams(searchParams)}`,
        },
    },
    schema_columns: {
        index: '/api/internal/ui/v1/schema/',
    },
    subscriptions: (searchParams: { stack: number; topic: string }) =>
        `/api/internal/ui/v1/subscriptions/${toSearchParams(searchParams)}`,
    target: {
        actions: {
            delete: '/api/web-app/targets/actions/delete/',
        },
        confirmDelete: '/api/web-app/targets/confirm-delete/',
        items: (searchParams: { stack_with_family: number }) => `/api/web-app/targets/${toSearchParams(searchParams)}`,
    },
    ui: {
        datastream: {
            autoMap: (datastreamId: DatastreamId) => `/api/internal/ui/v1/datastream/${datastreamId}/auto-map-columns/`,
            extracts: {
                actions: {
                    requeueSequential: (
                        datastreamId: DatastreamId,
                        searchParams?: {
                            extract_ids?: ReadonlyArray<number>
                            exclude_extract_ids?: ReadonlyArray<number>
                            aggregated_states?: ReadonlyArray<number>
                            binding_ids?: ReadonlyArray<number>
                            mode?: 'reload'
                        },
                    ) =>
                        `/api/internal/ui/v1/datastream/${datastreamId}/extracts/actions/requeue-sequential/${toSearchParams(searchParams)}`,
                },
            },
            show_looker_studio_after_transfer_info: (id: number) =>
                `/api/internal/ui/v1/datastream/${id}/looker_studio_onboarding_modal_info/`,
        },
        guided_onboarding: {
            getByWorkspace: (workspaceId: string) => `/api/internal/ui/v1/workspaces/${workspaceId}/guided_onboarding/`,
            index: (guidedOnboardingId: number | string) =>
                `/api/internal/ui/v1/guided_onboarding/${guidedOnboardingId}/`,
            sourceStep: (sourceId: number) => `/api/internal/ui/v1/guided_onboarding/source_step/${sourceId}/`,
        },
    },
    /**
     * @deprecated Use contract-based approach via @adverity/contracts:
     * - POST: api.POST('/api/web-app/google-data-studio/datasource/', { body: { name, target_id } })
     * - PATCH: api.PATCH('/api/web-app/google-data-studio/datasource/{datasourceId}/', { params: { path: { datasourceId } }, body: { name, datastudio_link } })
     *
     * Types are available from: import type { GoogleDataStudioDatasource } from '@adverity/contracts/contracts/legacy'
     */
    googleDataStudioDatasource: '/api/web-app/google-data-studio/datasource/',
    /**
     * @deprecated Use contract-based approach via @adverity/contracts:
     * - PATCH: api.PATCH('/api/web-app/google-data-studio/datasource/{datasourceId}/', { params: { path: { datasourceId } }, body: { name, datastudio_link } })
     *
     * Types are available from: import type { GoogleDataStudioDatasource } from '@adverity/contracts/contracts/legacy'
     */
    googleDataStudioDatasourceDetails: (datasourceId: number) =>
        `/api/web-app/google-data-studio/datasource/${datasourceId}/`,
    /**
     * @deprecated Use contract-based approach via @adverity/contracts:
     * - overview: apiOptions.query({ path: '/api/web-app/usage-manager/overview/' })
     * - rowActions: apiOptions.query({ path: '/api/web-app/usage-manager/row-actions/' })
     * - usageManagerExport: apiOptions.query({ path: '/api/web-app/usage-manager/row-actions/export/' })
     * - rowActionsStacks: apiOptions.query({ path: '/api/web-app/usage-manager/row-actions/stacks/' })
     * - rowActionsStack: apiOptions.query({ path: '/api/web-app/usage-manager/row-actions/stacks/{stack_id}/' })
     * - subscription: apiOptions.query({ path: '/api/web-app/usage-manager/subscription/' })
     *
     * Note: These endpoints are kept for backward compatibility. New code should use the contract.
     */
    usage_manager: {
        overview: '/api/web-app/usage-manager/overview/',
        rowActions: (searchParams: {
            date_from: string
            date_to: string
            metric: 'unique_rows' | 'total_rows'
            datastream_id: ReadonlyArray<number>
            stack_id: ReadonlyArray<number>
            datastream_type: ReadonlyArray<number>
            granularity: 'day' | 'month'
        }) => `/api/web-app/usage-manager/row-actions/${toSearchParams(searchParams)}`,
        usageManagerExport: (searchParams: {
            date_from: string
            date_to: string
            datastream_id: ReadonlyArray<number>
            stack_id: ReadonlyArray<number>
            datastream_type: ReadonlyArray<number>
            granularity: 'day' | 'month'
        }) => `/api/web-app/usage-manager/row-actions/export/${toSearchParams(searchParams)}`,
        rowActionsStacks: (searchParams: {
            date: string
            metric: 'unique_rows' | 'total_rows'
            datastream_id: ReadonlyArray<number>
            stack_id: ReadonlyArray<number>
            datastream_type: ReadonlyArray<number>
            granularity: 'day' | 'month'
        }) => `/api/web-app/usage-manager/row-actions/stacks/${toSearchParams(searchParams)}`,
        rowActionsStack: (
            stackId: number,
            searchParams: {
                date: string
                metric: 'unique_rows' | 'total_rows'
                datastream_id: ReadonlyArray<number>
                stack_id: ReadonlyArray<number>
                datastream_type: ReadonlyArray<number>
                granularity: 'day' | 'month'
            },
        ) => `/api/web-app/usage-manager/row-actions/stacks/${stackId}/${toSearchParams(searchParams)}`,
        subscription: '/api/web-app/usage-manager/subscription/',
    },
    /**
     * @deprecated Use contract-based approach via @adverity/contracts:
     * Import type { ApiResponseTypes } from '@adverity/contracts'
     * type WorkerActivityResponse = ApiResponseTypes['/api/web-app/workers_occupation/']
     *
     * Note: This endpoint is kept for backward compatibility. New code should use the contract.
     */
    worker_activity: '/api/web-app/workers_occupation/',
    /**
     * @deprecated Use contract-based approach via @adverity/contracts:
     * Import type { ApiResponseTypes } from '@adverity/contracts'
     * type InsightsWorkspaceMappingTable = ApiResponseTypes['/api/web-app/insights/workspace-mappings/'][number]
     *
     * Note: This endpoint is kept for backward compatibility. New code should use the contract.
     */
    workspaceMappingTable: '/api/web-app/insights/workspace-mappings/',
    /**
     * @deprecated Use contract-based approach via @adverity/contracts:
     * Import type { ApiResponseTypes } from '@adverity/contracts'
     * type InsightsWorkspaceMappingTable = ApiResponseTypes['/api/web-app/insights/workspace-mappings/{mappingTableId}/']
     *
     * Note: This endpoint is kept for backward compatibility. New code should use the contract.
     */
    workspaceMappingTableDetail: (mappingTableId: number) =>
        `/api/web-app/insights/workspace-mappings/${mappingTableId}`,
    /**
     * @deprecated Use contract-based approach via @adverity/contracts:
     * Import type { ApiResponseTypes } from '@adverity/contracts'
     * type InsightsWorkspaceMappingItem = ApiResponseTypes['/api/web-app/insights/workspace-mappings/{mappingTableId}/mappings/'][number]
     *
     * Note: This endpoint is kept for backward compatibility. New code should use the contract.
     */
    workspaceMappingTableDetailMappings: (
        mappingTableId: number,
        searchParams?: { ordering?: '-id' | 'id'; adverity_workspace__isnull?: boolean },
    ) => `/api/web-app/insights/workspace-mappings/${mappingTableId}/mappings/${toSearchParams(searchParams)}`,
    workspaces: (searchParams: { page_size: number; search?: string }) => `/api/stacks/${toSearchParams(searchParams)}`,
    workspaceDetail: (uuid: string) => `/api/stacks/${uuid}`,
} as const

export const VIEW_URLS = {
    create_destination: '/target/basetarget/add/',
}

export const MEASURES_MAP = {
    SUM: 'Sum',
    AVG: 'Average',
    COUNT: 'Count',
    MIN: 'Min',
    MAX: 'Max',
    NONAGG: "Don't aggregate",
}

export const DATA_TYPES = {
    1: 'String',
    2: 'Long',
    3: 'Float',
    4: 'Date',
    5: 'DateTime',
    6: 'Boolean',
    7: 'Percentage',
    8: 'JSON',
    9: 'Currency',
    10: 'Formula',
    11: 'Duration',
} as const

export const INSIGHTS_WORKSPACE_TYPES: Record<string, InsightsWorkspaceType> = {
    COMPANY: 1,
    BUSINESS_ENTITY: 2,
    CLIENT: 3,
} as const

export const detailsUrl = 'details/'

export const queryLargePageSize = 300

export const DATASTREAM_EXTRACT_STATES = {
    STATE_COLLECTED: 1,
    STATE_PROCESSED: 2,
    STATE_IN_TRANSIT: 3,
    STATE_IMPORT_ERROR: 4,
    STATE_QUEUED: 5,
    STATE_ENRICH_ERROR: 6,
    STATE_PENDING: 7,
    STATE_DELETED: 8,
    STATE_NO_DATA_COLLECTED: 9,
    STATE_DEFERRED: 10,
    STATE_PARKED: 11,
    STATE_SUCCESS: 12,
    STATE_FAILED: 13,
    STATE_RAW: 14,
    STATE_OVERWRITTEN: 15,
    STATE_PROCESSING_ERROR: 16,
    STATE_PII_REMOVED: 17,
} as const
